'use client';
import { createStyles, xref, rem, getSize } from '@s7/styles';
import { isObject } from '@s7/utils';
import { defaultStylesParams } from './defaults.js';
import { IDENT } from './IconButton.contract.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const iconSizes = {
    xxs1: 24,
    xs1: 32,
    xs2: 36,
    sm1: 40,
    md1: 48,
    lg1: 56
};
const xrefIcon = xref(IDENT, 'icon');
const xclassIcon = `.${xrefIcon}`;
const xrefLabel = xref(IDENT, 'label');
const xclassLabel = `.${xrefLabel}`;
const isShadesIndependentVariant = (variant)=>variant === 'white' || variant === 'secondary' || variant === 'ghost';
const containerVariantStyles = {
    default: (theme, shades)=>{
        const base = shades.base || shades[600];
        return _extends({
            default: base
        }, theme.fn.deriveStates(base, 'color'), {
            focus: shades.mid || shades[200]
        });
    },
    light: (theme, shades)=>{
        const base = shades.base || shades[600];
        return _extends({
            default: shades.min || shades[50]
        }, theme.fn.deriveStates(base, 'color'), {
            hover: base,
            focus: shades.mid || shades[200]
        });
    },
    white: (theme)=>({
            default: theme.palette.button.white,
            focus: theme.palette.border.element
        }),
    ghost: (theme)=>_extends({
            default: 'transparent'
        }, theme.fn.deriveStates('transparent', 'light'), {
            focus: theme.palette.border.element
        }),
    secondary: (theme)=>_extends({
            default: theme.palette.button.secondary
        }, theme.fn.deriveStates(theme.palette.button.secondary, 'light'), {
            focus: theme.palette.border.element
        })
};
const iconBackgrounds = {
    default: (_, shades)=>shades.base || shades[600],
    light: (_, shades)=>shades.min || shades[50],
    white: (theme)=>theme.palette.button.white,
    ghost: ()=>'transparent',
    secondary: (theme)=>theme.palette.button.secondary
};
const getIconBackground = (theme, shades, variant = 'default')=>{
    return (iconBackgrounds[variant] || iconBackgrounds.default)(theme, shades);
};
const getContainerVariantStyles = (theme, shades, variant = 'default')=>{
    return (containerVariantStyles[variant] || containerVariantStyles.default)(theme, shades);
};
const getLabelStyles = (theme, shades, variant = 'default')=>{
    if (isShadesIndependentVariant(variant)) {
        return theme.palette.text.medium;
    }
    return shades.base || shades[600];
};
const getIconColor = (theme, shades, variant = 'default', state = 'default')=>{
    if (isShadesIndependentVariant(variant)) {
        return theme.palette.text.medium;
    }
    const base = shades.base || shades[600];
    return variant === 'default' ? theme.palette.text.inverted : state === 'default' ? base : theme.palette.text.onColor;
};
const getDisabledStyles = (theme, variant = 'default')=>({
        pointerEvents: 'none',
        [xclassIcon]: {
            background: variant === 'ghost' ? 'none' : theme.palette.background.highlight,
            color: variant === 'ghost' ? theme.palette.text.quaternary : theme.palette.text.tertiary
        },
        [xclassLabel]: {
            color: variant === 'ghost' ? theme.palette.text.quaternary : theme.palette.text.tertiary
        }
    });
const getShades = (theme, color)=>theme.fn.deriveShades(theme.fn.themeColor(color, theme.palette.primary));
const getColorStyles = (color, typographyColor)=>{
    return isObject(typographyColor) || !typographyColor ? color : typographyColor;
};
const rootStyles = (theme, params)=>{
    const { color, typography: _typography, variant } = _extends({}, defaultStylesParams, params);
    const _ref = _typography || {}, { color: typographyColor } = _ref, typography = _object_without_properties_loose(_ref, [
        "color"
    ]);
    const colored = getColorStyles(color, typographyColor);
    const shades = getShades(theme, colored);
    const container = getContainerVariantStyles(theme, shades, variant);
    const iconColor = colored && isShadesIndependentVariant(variant) ? theme.fn.primaryShade(theme.fn.themeColor(colored)) : getIconColor(theme, shades, variant, 'hover');
    const overlay = variant === 'white' && !colored ? 'dark' : 'color';
    const iconOverlays = theme.fn.deriveStates(iconColor, overlay);
    const labelColor = colored ? theme.fn.primaryShade(theme.fn.themeColor(colored)) : getLabelStyles(theme, shades, variant);
    const labelOverlays = theme.fn.deriveStates(labelColor, overlay);
    return _extends({}, theme.fn.typography(_extends({
        size: 'sm1',
        weight: 'semibold'
    }, typography)), theme.fn.focusRing({
        color: container.focus,
        selector: `&:focus-visible ${xclassIcon}`
    }), {
        '&:hover': {
            [xclassIcon]: {
                background: variant === 'white' ? container.default : container.hover,
                color: isShadesIndependentVariant(variant) ? iconOverlays.hover : iconColor
            },
            [xclassLabel]: {
                color: labelOverlays.hover
            }
        },
        '&:active': {
            [xclassIcon]: {
                background: variant === 'white' ? container.default : container.active,
                color: isShadesIndependentVariant(variant) ? iconOverlays.active : iconColor
            },
            [xclassLabel]: {
                color: labelOverlays.active
            }
        },
        '&:disabled,&[data-disabled=true]': getDisabledStyles(theme, variant)
    });
};
const sharedTransitionProps = {
    props: [
        'color',
        'background-color',
        'border-radius'
    ],
    fn: 'ease-in-out'
};
const labelStyles = (theme, params)=>{
    const { transitionDuration, color, variant, typography } = _extends({}, defaultStylesParams, params);
    const textColor = getColorStyles(color, typography == null ? void 0 : typography.color);
    const shades = getShades(theme, textColor);
    return _extends({
        color: textColor ? theme.fn.primaryShade(theme.fn.themeColor(textColor)) : getLabelStyles(theme, shades, variant)
    }, theme.fn.transition({
        props: 'color',
        fn: 'ease-in-out',
        duration: transitionDuration
    }));
};
const iconStyles = (theme, params)=>{
    const { transitionDuration, variant, size, color, radius, typography } = _extends({}, defaultStylesParams, params);
    const iconColor = getColorStyles(color, typography == null ? void 0 : typography.color);
    const shades = getShades(theme, iconColor);
    return _extends({
        background: getIconBackground(theme, shades, variant),
        color: iconColor && isShadesIndependentVariant(variant) ? theme.fn.primaryShade(theme.fn.themeColor(iconColor)) : getIconColor(theme, shades, variant)
    }, theme.fn.transition(_extends({}, sharedTransitionProps, {
        duration: transitionDuration
    })), theme.fn.square({
        size: rem(getSize({
            size,
            sizes: iconSizes
        })),
        radius: theme.fn.borderRadius(radius)
    }));
};
var useStyles = createStyles((theme, _param, _)=>{
    var { breakpoints } = _param, params = _object_without_properties_loose(_param, [
        "breakpoints"
    ]);
    return {
        root: _extends({
            position: 'relative',
            userSelect: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'fit-content'
        }, rootStyles(theme, params), theme.fn.responsive(breakpoints)((params)=>rootStyles(theme, params))),
        label: _extends({
            ref: xrefLabel,
            lineHeight: '100%'
        }, labelStyles(theme, params), theme.fn.responsive(breakpoints)((params)=>labelStyles(theme, params))),
        icon: _extends({
            ref: xrefIcon,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }, iconStyles(theme, params), theme.fn.responsive(breakpoints)((params)=>iconStyles(theme, params))),
        leftIcon: {},
        rightIcon: {}
    };
});

export { useStyles as default };
//# sourceMappingURL=IconButton.styles.js.map
